import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, PLATFORM_ID, computed, effect, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ReviewApiService } from '../core/api/review-api.service';
import { CustomerAuthService } from '../core/auth/customer-auth.service';

const STORAGE_KEY = 'unaavu.reviewed-order-ids';

const readReviewedIds = (): string[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const rememberReviewed = (orderId: string): void => {
  const next = Array.from(new Set([...readReviewedIds(), orderId]));
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
};

@Component({
  selector: 'app-review-nudge',
  standalone: true,
  imports: [MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule],
  templateUrl: './review-nudge.html',
  styleUrl: './review-nudge.scss',
})
export class ReviewNudgeComponent {
  private readonly reviewApi = inject(ReviewApiService);
  private readonly customerAuth = inject(CustomerAuthService);
  private readonly platformId = inject(PLATFORM_ID);

  readonly orderId = input<string | null>(null);
  readonly dishNames = input<string[]>([]);
  readonly guestNameHint = input('');

  readonly rating = signal(5);
  readonly guestName = signal('');
  readonly comment = signal('');
  readonly submitting = signal(false);
  readonly submitted = signal(false);
  readonly error = signal<string | null>(null);
  readonly dismissed = signal(false);

  readonly isPrivateFeedback = computed(() => this.rating() < 4);
  readonly visible = computed(() => {
    if (!isPlatformBrowser(this.platformId) || this.dismissed() || this.submitted()) {
      return false;
    }
    const orderId = this.orderId();
    if (orderId && readReviewedIds().includes(orderId)) {
      return false;
    }
    return true;
  });

  readonly headline = computed(() => {
    const dishes = this.dishNames().slice(0, 2);
    if (dishes.length === 0) {
      return 'How was Unaavu this time?';
    }
    if (dishes.length === 1) {
      return `You ordered ${dishes[0]}. How did it taste?`;
    }
    return `You ordered ${dishes[0]} and ${dishes[1]}. How did you like it?`;
  });

  constructor() {
    effect(() => {
      const hint = this.guestNameHint() || this.customerAuth.currentCustomer()?.name?.trim() || '';
      if (hint && !this.guestName()) {
        this.guestName.set(hint);
      }
    });
  }

  setRating = (value: number): void => {
    this.rating.set(value);
  };

  onNameInput = (event: Event): void => {
    this.guestName.set((event.target as HTMLInputElement).value);
  };

  onCommentInput = (event: Event): void => {
    this.comment.set((event.target as HTMLTextAreaElement).value);
  };

  dismiss = (): void => {
    this.dismissed.set(true);
  };

  submit = (): void => {
    this.error.set(null);
    const guestName = this.guestName().trim();
    const comment = this.comment().trim();
    if (guestName.length < 2 || comment.length < 10) {
      this.error.set(
        this.isPrivateFeedback()
          ? 'Please tell us your name and what we should fix.'
          : 'Please share your name and a few words about the meal.',
      );
      return;
    }
    this.submitting.set(true);
    const orderId = this.orderId();
    this.reviewApi
      .create({
        guestName,
        rating: this.rating(),
        comment,
        ...(orderId ? { orderId } : {}),
      })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.submitted.set(true);
          if (isPlatformBrowser(this.platformId) && orderId) {
            rememberReviewed(orderId);
          }
        },
        error: (err: unknown) => {
          this.submitting.set(false);
          this.error.set(
            err instanceof HttpErrorResponse
              ? 'We could not save that just now. Please try again.'
              : 'We could not save that just now. Please try again.',
          );
        },
      });
  };
}
